import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, set, get } from '@ember/object';
import { timeout, restartableTask, dropTask } from 'ember-concurrency';
import zft from 'garaje/utils/zero-for-tests';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import LocationValidations, { MIN_CAPACITY_LIMIT, MAX_CAPACITY_LIMIT } from 'garaje/validations/location';
import { isPresent } from '@ember/utils';
import { service } from '@ember/service';

/**
 * @param {Function} registerChangeset method to register the changeset on the controller for dirty transition checking
 * @param {Function} onSearchUsers searches users by term
 * @param {Class<Location>} location Location
 * @param {Boolean} [disabled] If true, open button is disabled
 */
export default class VisitorsSettingsCapacityCapacityLimitComponent extends Component {
  minCapacityLimit = MIN_CAPACITY_LIMIT;
  maxCapacityLimit = MAX_CAPACITY_LIMIT;

  @service statsig;

  @tracked isSettingsPanelOpen = false;

  constructor() {
    super(...arguments);
    this.changeset = new Changeset(this.args.location, lookupValidator(LocationValidations), LocationValidations);
    this.args.registerChangeset(this.changeset);
  }

  /**
   * Ensures that PowerSelectMultiple never holds a reference to the capacityContacts hasMany relationship
   *
   * @returns {Array} List of selected capacityContacts user models in changeset
   */
  get selectedCapacityContacts() {
    return get(this.changeset, 'capacityContacts').slice();
  }

  /**
   * Sets the setCapacityLimit value in the location changeset
   *
   * @param {string} capacityLimit Capacity limit input value
   */
  @action
  setCapacityLimit(capacityLimit) {
    set(this.changeset, 'capacityLimit', parseInt(capacityLimit) || null);
  }

  /**
   * Sets the capacityContacts value in the location changeset
   *
   * @param {Array} capacityContacts List of user models to save as capacity contacts
   */
  @action
  setCapacityContacts(capacityContacts) {
    set(this.changeset, 'capacityContacts', capacityContacts);
  }

  /**
   * Constrains the capacityLimit input to MAX and MIN values
   *
   * @param {object} event input event
   * @param {object} event.target capacity limit input element
   */
  @action
  constrainCapacity({ target }) {
    let { valueAsNumber } = target;
    valueAsNumber = Math.min(valueAsNumber, MAX_CAPACITY_LIMIT);
    valueAsNumber = Math.max(valueAsNumber, MIN_CAPACITY_LIMIT);
    target.value = valueAsNumber;
  }

  /**
   * Shows settings panel
   */
  @action
  showSettings() {
    this.isSettingsPanelOpen = true;
  }

  /**
   * Hides settings panel
   */
  @action
  hideSettings() {
    this.isSettingsPanelOpen = false;
  }

  /**
   * Rolls back the changeset
   */
  @action
  rollback() {
    this.changeset.rollback();
  }

  /**
   * Task for searching users
   *
   * @param {string} term Search term
   * @returns {Promise<Array>} Promise for an array of search results
   */
  @restartableTask
  *searchUsersTask(term) {
    yield timeout(zft(250));
    const capacityContacts = get(this.changeset, 'capacityContacts');
    const users = yield this.args.onSearchUsers(term);
    return users.reject((user) => capacityContacts.includes(user));
  }

  /**
   * Task for saving location model changes
   *
   * @param {object} event DOM event
   */
  @dropTask
  *saveTask(event) {
    event.preventDefault();
    const { changeset } = this;
    const { onSubmit, onConfirmCapacity } = this.args;
    const { capacityLimit } = changeset.change;
    yield changeset.validate();

    let shouldSaveChanges = changeset.isValid;
    if (isPresent(capacityLimit)) {
      shouldSaveChanges = yield onConfirmCapacity(capacityLimit);
    }
    if (shouldSaveChanges) {
      yield onSubmit(changeset);
    }
  }

  /**
   * Task for disabling capacityLimit and saving location model
   *
   * @param {object} event DOM event
   */
  @dropTask
  *disableTask(event) {
    const { changeset } = this;
    set(changeset, 'capacityLimit', null);
    yield changeset.validate();
    changeset.rollbackInvalid();
    yield this.saveTask.perform(event);
    this.hideSettings();
  }
}
